import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Filter, User, MapPin, X, Loader, Users, Music } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { ALL_PROVINCES, getProvinceName } from '../utils/provinces';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const INSTRUMENTS = [
  'Zang', 'Gitaar', 'Basgitaar', 'Drums', 'Keyboard', 'Piano',
  'Saxofoon', 'Trompet', 'Viool', 'Cello', 'DJ', 'Productie', 'Anders'
];

const GENRES = [
  'Rock', 'Pop', 'Metal', 'Jazz', 'Blues', 'Funk', 'Soul', 'R&B',
  'Hip-hop', 'Electronic', 'Folk', 'Country', 'Klassiek', 'Punk', 'Indie', 'Reggae'
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [ads, setAds] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    province: '',
    lookingFor: '',
    genre: '',
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const adsSnap = await get(ref(database, 'vjb_ads'));
        const profilesSnap = await get(ref(database, 'vjb_profiles'));
        
        const profilesData = profilesSnap.exists() ? profilesSnap.val() : {};
        setProfiles(profilesData);
        
        if (adsSnap.exists()) {
          const adsData = adsSnap.val();
          const adsList = Object.entries(adsData)
            .map(([id, ad]) => ({ id, ...ad }))
            .filter(ad => ad.status === 'active')
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setAds(adsList);
        }
      } catch (error) {
        console.error('Error loading ads:', error);
      }
      setLoading(false);
    };
    
    loadData();
  }, []);

  useEffect(() => {
    const params = {};
    if (searchQuery) params.q = searchQuery;
    if (filters.type) params.type = filters.type;
    setSearchParams(params);
  }, [searchQuery, filters.type, setSearchParams]);

  const getCreatorInfo = (ad) => {
    const profile = profiles[ad.createdBy];
    return {
      name: profile?.displayName || 'Onbekend',
      photoUrl: profile?.photoUrl || null,
      type: profile?.type || 'musician'
    };
  };

  const filteredAds = ads.filter(ad => {
    // Type filter (band or musician)
    if (filters.type) {
      const creator = getCreatorInfo(ad);
      if (filters.type === 'band' && creator.type !== 'band') return false;
      if (filters.type === 'musician' && creator.type === 'band') return false;
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = ad.title?.toLowerCase().includes(query);
      const matchesDescription = ad.description?.toLowerCase().includes(query);
      const matchesLookingFor = ad.lookingFor?.some(i => i.toLowerCase().includes(query));
      const matchesGenres = ad.genres?.some(g => g.toLowerCase().includes(query));
      const matchesCity = ad.location?.city?.toLowerCase().includes(query);
      const matchesProvince = getProvinceName(ad.location?.province)?.toLowerCase().includes(query);
      const creator = getCreatorInfo(ad);
      const matchesCreator = creator.name?.toLowerCase().includes(query);
      
      if (!matchesTitle && !matchesDescription && !matchesLookingFor && !matchesGenres && !matchesCity && !matchesProvince && !matchesCreator) {
        return false;
      }
    }
    
    if (filters.province && ad.location?.province !== filters.province) return false;
    if (filters.lookingFor && !ad.lookingFor?.includes(filters.lookingFor)) return false;
    if (filters.genre && !ad.genres?.includes(filters.genre)) return false;
    
    return true;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({ type: '', province: '', lookingFor: '', genre: '' });
  };

  const hasActiveFilters = searchQuery || filters.type || filters.province || filters.lookingFor || filters.genre;
  const activeFilterCount = [filters.type, filters.province, filters.lookingFor, filters.genre].filter(Boolean).length;

  const getTypeLabel = () => {
    if (filters.type === 'band') return 'Band zoekt muzikant';
    if (filters.type === 'musician') return 'Muzikant zoekt band';
    return 'Alle oproepen';
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      {/* Type Toggle */}
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex gap-2 py-4 overflow-x-auto">
            <button
              onClick={() => setFilters({...filters, type: ''})}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                !filters.type
                  ? 'bg-[#8146FF] text-white'
                  : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'
              }`}
            >
              Alle oproepen
            </button>
            <button
              onClick={() => setFilters({...filters, type: 'band'})}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                filters.type === 'band'
                  ? 'bg-[#8146FF] text-white'
                  : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'
              }`}
            >
              <Users size={16} />
              Band zoekt muzikant
            </button>
            <button
              onClick={() => setFilters({...filters, type: 'musician'})}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                filters.type === 'musician'
                  ? 'bg-[#8146FF] text-white'
                  : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'
              }`}
            >
              <Music size={16} />
              Muzikant zoekt band
            </button>
          </div>
        </div>
      </div>

      {/* Search Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search input */}
            <div className="flex-1 relative">
              <SearchIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek op instrument, genre, locatie..."
                className="w-full bg-white/10 border border-white/20 rounded-full pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]"
              />
            </div>
            
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-full border transition ${
                showFilters || activeFilterCount > (filters.type ? 1 : 0)
                  ? 'bg-[#8146FF] border-[#8146FF] text-white'
                  : 'bg-white/10 border-white/20 text-white/70 hover:text-white'
              }`}
            >
              <Filter size={18} />
              <span>Filters</span>
              {activeFilterCount > (filters.type ? 1 : 0) && (
                <span className="bg-white text-[#8146FF] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {activeFilterCount - (filters.type ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="grid md:grid-cols-3 gap-4">
                <select
                  value={filters.province}
                  onChange={(e) => setFilters({...filters, province: e.target.value})}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]"
                >
                  <option value="" className="bg-gray-900">Alle provincies</option>
                  {ALL_PROVINCES.map(p => (
                    <option key={p.code} value={p.code} className="bg-gray-900">{p.name}</option>
                  ))}
                </select>

                <select
                  value={filters.lookingFor}
                  onChange={(e) => setFilters({...filters, lookingFor: e.target.value})}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]"
                >
                  <option value="" className="bg-gray-900">Zoekt instrument...</option>
                  {INSTRUMENTS.map(i => (
                    <option key={i} value={i} className="bg-gray-900">{i}</option>
                  ))}
                </select>

                <select
                  value={filters.genre}
                  onChange={(e) => setFilters({...filters, genre: e.target.value})}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]"
                >
                  <option value="" className="bg-gray-900">Alle genres</option>
                  {GENRES.map(g => (
                    <option key={g} value={g} className="bg-gray-900">{g}</option>
                  ))}
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-[#8146FF] hover:text-[#a074ff] text-sm flex items-center gap-1"
                >
                  <X size={14} />
                  Filters wissen
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <main className="flex-1 max-w-6xl mx-auto px-4 md:px-6 py-8 w-full">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-[#8146FF]" size={32} />
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
              <SearchIcon size={32} className="text-white/20" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Geen oproepen gevonden</h3>
            <p className="text-white/50 mb-6">
              {hasActiveFilters ? 'Probeer andere zoektermen of filters' : 'Er zijn nog geen oproepen geplaatst'}
            </p>
            {hasActiveFilters ? (
              <button onClick={clearFilters} className="text-[#8146FF] hover:text-[#a074ff] font-medium">
                Filters wissen
              </button>
            ) : (
              <Link to={user ? "/oproep/nieuw" : "/inloggen"} className="inline-block bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition">
                Plaats de eerste oproep
              </Link>
            )}
          </div>
        ) : (
          <>
            <p className="text-white/50 text-sm mb-6">
              {filteredAds.length} oproep{filteredAds.length !== 1 ? 'en' : ''} gevonden
              {filters.type && <span className="text-white/40"> in {getTypeLabel().toLowerCase()}</span>}
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAds.map((ad) => {
                const creator = getCreatorInfo(ad);
                return (
                  <Link 
                    key={ad.id}
                    to={`/oproep/${ad.id}`}
                    className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8146FF]/50 rounded-2xl overflow-hidden transition-all duration-300"
                  >
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#8146FF]/30 to-black relative overflow-hidden">
                      {creator.photoUrl ? (
                        <img 
                          src={creator.photoUrl} 
                          alt={creator.name}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <User size={48} className="text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
                        {ad.lookingFor?.slice(0, 2).map((item, idx) => (
                          <span key={idx} className="bg-[#8146FF] text-white text-xs font-medium px-2 py-1 rounded-full">{item}</span>
                        ))}
                        {ad.lookingFor?.length > 2 && (
                          <span className="bg-white/20 text-white text-xs font-medium px-2 py-1 rounded-full">+{ad.lookingFor.length - 2}</span>
                        )}
                      </div>
                      {/* Type badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          creator.type === 'band' 
                            ? 'bg-purple-500/80 text-white' 
                            : 'bg-blue-500/80 text-white'
                        }`}>
                          {creator.type === 'band' ? 'Band' : 'Muzikant'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-[#8146FF] transition line-clamp-1">{ad.title}</h3>
                      <p className="text-white/50 text-sm mb-4 line-clamp-2">{ad.description}</p>
                      {ad.genres?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {ad.genres.slice(0, 3).map((genre, idx) => (
                            <span key={idx} className="bg-white/10 text-white/60 text-xs px-2 py-0.5 rounded">{genre}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {creator.photoUrl ? (
                            <img src={creator.photoUrl} alt={creator.name} className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                              <User size={12} className="text-white/40" />
                            </div>
                          )}
                          <span className="text-white/60 text-sm">{creator.name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-white/40 text-sm">
                          <MapPin size={12} />
                          {ad.location?.city || getProvinceName(ad.location?.province)}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
